import { createSystemFont, defaultConfig } from '@tamagui/config/v4'
import { createTamagui, createTokens } from 'tamagui'
import { fontFamilies } from '@/src/common/theme'
import { createAnimations } from '@tamagui/animations-moti'
import { Easing } from 'react-native-reanimated'

const easeIn = Easing.in(Easing.ease)
const smoothBezier = Easing.bezier(0.215, 0.61, 0.355, 1.0)

/** @see {@link https://github.com/tamagui/tamagui/issues/2841#issuecomment-3014967460} */
const motiAnalogsOfTamaguiDefaultAnimations = createAnimations({
  '75ms': { type: 'timing', duration: 75, easing: easeIn },
  '100ms': { type: 'timing', duration: 100, easing: easeIn },
  '200ms': { type: 'timing', duration: 200, easing: easeIn },
  bouncy: { type: 'timing', duration: 200, easing: easeIn },
  superBouncy: { type: 'timing', duration: 500, easing: easeIn },
  lazy: { type: 'timing', duration: 1000, easing: easeIn },
  medium: { type: 'timing', duration: 300, easing: easeIn },
  slow: { type: 'timing', duration: 500, easing: easeIn },
  quick: { type: 'timing', duration: 400, easing: smoothBezier },
  quicker: { type: 'timing', duration: 300, easing: smoothBezier },
  quickest: { type: 'timing', duration: 200, easing: smoothBezier },
  tooltip: { type: 'timing', duration: 400, easing: easeIn },
} as const)

/** @see {@link https://tamagui.dev/docs/intro/styles} */
export const groupNames = {
  cardHolderAgreement: 'cardHolderAgreement',
  accountListItemRow: 'accountListItemRow',
} as const satisfies Record<string, string>

groupNames satisfies { [key in keyof typeof groupNames]: key }

const tokens = createTokens({
  ...defaultConfig.tokens,
  color: {
    background: '#FFFFFF',
    backgroundDark: '#223843',
    border: '#CDD4D5',
    text: '#152438',
    textHeading: '#223843',
    textDark: '#FFFFFF',
    textHeadingDark: '#FFFFFF',
    textDisabled: '#898D8F',
    textSubtle: '#A4AAAC',
    textError: '#DB340B',
    primary: '#0C9DB0',
    secondary: '#00dfc9',
    tertiary: '#fff1ca',
    oldPrimary: '#00DFC9',
  },
})

export const Tamagui = createTamagui({
  ...defaultConfig,
  tokens,
  animations: {
    ...defaultConfig.animations,
    ...motiAnalogsOfTamaguiDefaultAnimations,
  },
  fonts: {
    ...defaultConfig.fonts,
    body: createSystemFont({
      font: { family: fontFamilies['PlusJakartaSans-Regular'], weight: { 4: '400' } },
    }),
    heading: createSystemFont({
      font: { family: fontFamilies['PlusJakartaSans-Bold'], weight: { 4: '700' } },
    }),
  },
  themes: {
    ...defaultConfig.themes,
    light: {
      ...defaultConfig.themes.light,
      background: tokens.color.background,
      color: tokens.color.text,
      primary: tokens.color.primary,
      secondary: tokens.color.secondary,
      tertiary: tokens.color.tertiary,
      oldPrimary: tokens.color.oldPrimary,
    },
    dark: {
      ...defaultConfig.themes.dark,
      background: tokens.color.backgroundDark,
      color: tokens.color.textDark,
      primary: tokens.color.primary,
      secondary: tokens.color.secondary,
      tertiary: tokens.color.tertiary,
      oldPrimary: tokens.color.oldPrimary,
    },
  },
})

export default Tamagui

export type Conf = typeof Tamagui

declare module 'tamagui' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface TamaguiCustomConfig extends Conf {}

  interface TypeOverride {
    groupNames(): keyof typeof groupNames
  }
}
